<template>
  <v-card class="fill-height" flat outlined>

    <!-- Contract Details header -->
    <div class="grey lighten-5 pa-4 pb-3" style="border-bottom: 2px solid var(--v-primary-base)">
      <div class="d-flex align-center mb-3">
        <span class="text-subtitle-1 font-weight-bold">Contract Details</span>
        <v-spacer />
        <v-btn icon small @click="headerExpanded = !headerExpanded">
          <v-icon>{{ headerExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="headerExpanded">
          <v-row class="mb-4">
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Contract ID</div>
              <div class="text-body-2">{{ contractDetails.contract_id || '-' }}</div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Job Type</div>
              <v-chip
                v-if="contractDetails.extra_info && contractDetails.extra_info.job_type"
                x-small color="primary"
              >{{ contractDetails.extra_info.job_type }}</v-chip>
              <span v-else class="text-body-2">-</span>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Vendor</div>
              <div class="text-body-2">
                {{ contractDetails.transporter_detail && contractDetails.transporter_detail.company_name || '-' }}
              </div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Start Date</div>
              <div class="text-body-2">
                {{ contractDetails.extra_info && contractDetails.extra_info.start_date | formatDate }}
              </div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">End Date</div>
              <div class="text-body-2">
                {{ contractDetails.extra_info && contractDetails.extra_info.end_date | formatDate }}
              </div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Status</div>
              <v-chip
                v-if="contractDetails.status"
                x-small
                :color="contractDetails.status === 'Active' ? 'success' : contractDetails.status === 'Closed' ? 'error' : 'warning'"
                text-color="white"
              >{{ contractDetails.status }}</v-chip>
              <span v-else class="text-body-2">-</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Created On</div>
              <div class="text-body-2">
                {{ contractDetails.extra_info && contractDetails.extra_info.created | formatDate(true) }}
              </div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Created By</div>
              <div class="text-body-2">{{ contractDetails.extra_info && contractDetails.extra_info.created_by || '-' }}</div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Modified On</div>
              <div class="text-body-2">
                {{ contractDetails.extra_info && contractDetails.extra_info.modified | formatDate(true) }}
              </div>
            </v-col>
            <v-col cols="2">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Modified By</div>
              <div class="text-body-2">{{ contractDetails.extra_info && contractDetails.extra_info.updated_by || '-' }}</div>
            </v-col>
            <v-col cols="4">
              <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">Remarks</div>
              <div class="text-body-2">
                {{ contractDetails.remarks_detail && contractDetails.remarks_detail.admin_remarks || '-' }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
    </div>

    <!-- Tabs: Rates / Charges -->
    <v-tabs v-model="activeTab" color="primary" class="px-4 pt-2">
      <v-tab>Contract Rates</v-tab>
      <v-tab>Contract Charges</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Rates tab -->
      <v-tab-item>
        <div class="pa-4">
          <div class="d-flex align-center mb-3">
            <v-text-field
              v-model="rateSearch"
              label="Search Route"
              outlined
              dense
              clearable
              hide-details
              append-icon="mdi-magnify"
              style="width:220px"
              @input="onRateSearch"
            />
            <v-spacer />
            <div class="d-flex align-center" style="gap:8px">
              <v-btn
                v-if="permissions && permissions.add"
                small outlined color="primary"
                @click="openAddRateDialog"
              >
                <v-icon small left>mdi-plus</v-icon>Add Rate
              </v-btn>
              <v-btn small outlined color="primary" @click="bulkUploadDialog = true">
                <v-icon small left>mdi-upload</v-icon>Bulk Upload
              </v-btn>
              <v-btn small outlined color="primary" @click="exportRates">
                <v-icon small left>mdi-export</v-icon>Export Rates
              </v-btn>
              <v-btn small outlined color="deep-purple" @click="logsDialog = true">
                <v-icon small left>mdi-format-list-bulleted</v-icon>Logs
              </v-btn>
            </div>
          </div>

          <AgGridVue
            style="width:100%;height:calc(100vh - 420px)"
            class="ag-theme-alpine"
            :columnDefs="rateColumns"
            :rowData="contractRates"
            :context="context"
            :defaultColDef="{ resizable: true, sortable: true }"
            :gridOptions="gridOptions"
            @grid-ready="onGridReady"
          />

          <div v-if="ratesTotalItems > 0" class="d-flex justify-end mt-2">
            <Pagination
              :pageNo="ratesPageNo"
              :totalItems="ratesTotalItems"
              :pageSize="ratesPerPage"
              @itemsPerPageChange="ratesPerPageChanged"
              @prevPage="ratesPrevPage"
              @nextPage="ratesNextPage"
            />
          </div>
        </div>
      </v-tab-item>

      <!-- Charges tab -->
      <v-tab-item>
        <div class="pa-4">
          <div class="d-flex align-center mb-3">
            <v-spacer />
            <v-btn
              v-if="permissions && permissions.add && contractDetails.status === 'Active'"
              small outlined color="primary"
              @click="openAddChargeDialog"
            >
              <v-icon small left>mdi-plus</v-icon>Add Charge
            </v-btn>
          </div>

          <AgGridVue
            style="width:100%;height:calc(100vh - 420px)"
            class="ag-theme-alpine"
            :columnDefs="chargeColumns"
            :rowData="contractCharges"
            :context="context"
            :defaultColDef="{ resizable: true, sortable: true }"
            :gridOptions="chargeGridOptions"
            @grid-ready="onChargeGridReady"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- Add/Edit Charge dialog -->
    <v-dialog v-model="chargeDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="secondary py-2 white--text">
          <span class="text-subtitle-1 font-weight-medium">{{ editingCharge.id ? 'Edit Charge' : 'Add Charge' }}</span>
          <v-spacer />
          <v-btn icon dark @click="closeChargeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="chargeForm" v-model="chargeFormValid">
            <v-row>
              <v-col cols="12">
                <MultiSelect
                  label="Charge *"
                  :items="allCharges"
                  itemText="charge_title"
                  itemValue="id"
                  :rules="[(v) => !!v || 'Required']"
                  hide-details="auto"
                  :returnObject="true"
                  v-model="selectedCharge"
                  :error-messages="chargeFormErrors.charge"
                  @input="onChargeSelect"
                />
              </v-col>
              <v-col cols="12">
                <InputField
                  label="Description"
                  hide-details="auto"
                  readonly
                  v-model="editingCharge.description"
                />
              </v-col>
              <v-col cols="12" md="4">
                <InputField
                  label="Revenue GL No."
                  hide-details="auto"
                  readonly
                  v-model="editingCharge.gl_no"
                />
              </v-col>
              <v-col cols="12" md="4">
                <InputField
                  label="Cost GL No."
                  hide-details="auto"
                  readonly
                  v-model="editingCharge.cost_gl_no"
                />
              </v-col>
              <v-col cols="12" md="6">
                <InputField
                  label="Tax (%)"
                  hide-details="auto"
                  readonly
                  v-model="editingCharge.tax_percentage"
                />
              </v-col>
              <v-col cols="12">
                <InputField
                  type="number"
                  label="Rate *"
                  :rules="[(v) => !!v || 'Required', (v) => v > 0 || 'Must be greater than 0']"
                  hide-details="auto"
                  v-model="editingCharge.rate"
                  :error-messages="chargeFormErrors.rate"
                  @input="delete chargeFormErrors.rate"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" depressed :disabled="!chargeFormValid" @click="submitCharge">
            {{ editingCharge.id ? 'Update' : 'Add' }}
          </v-btn>
          <v-btn depressed @click="closeChargeDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Rate dialog -->
    <v-dialog v-model="rateDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="secondary py-2 white--text">
          <span class="text-subtitle-1 font-weight-medium">{{ editingRate.id ? 'Edit Rate' : 'Add Rate' }}</span>
          <v-spacer />
          <v-btn icon dark @click="closeRateDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="rateForm" v-model="rateFormValid">
            <v-row>
              <v-col cols="12">
                <Autocomplete
                  label="Route *"
                  :items="allLaneList"
                  itemText="title"
                  itemValue="id"
                  :loading="laneLoading"
                  :totalItems="laneCount"
                  @updateList="getLaneList"
                  hide-details="auto"
                  v-model="editingRate.lane"
                  :error-messages="rateFormErrors.lane"
                  @input="delete rateFormErrors.lane"
                />
              </v-col>
              <v-col cols="12" md="6">
                <MultiSelect
                  label="Container Type *"
                  :items="allContainerTypes"
                  itemText="type_name"
                  itemValue="id"
                  :rules="[(v) => !!v || 'Required']"
                  hide-details="auto"
                  v-model="editingRate.container_type"
                  :error-messages="rateFormErrors.container_type"
                  @input="delete rateFormErrors.container_type"
                />
              </v-col>
              <v-col cols="12" md="6">
                <InputField
                  type="number"
                  label="Count *"
                  :rules="[(v) => !!v || 'Required', (v) => v <= 4 || 'Max count is 4']"
                  hide-details="auto"
                  v-model="editingRate.container_count"
                  :error-messages="rateFormErrors.container_count"
                  @input="delete rateFormErrors.container_count"
                />
              </v-col>
              <v-col cols="12">
                <InputField
                  type="number"
                  label="Rate *"
                  :rules="[(v) => !!v || 'Required']"
                  hide-details="auto"
                  v-model="editingRate.rate"
                  :error-messages="rateFormErrors.rate"
                  @input="delete rateFormErrors.rate"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" depressed :disabled="!rateFormValid" @click="submitRate">
            {{ editingRate.id ? 'Update' : 'Add' }}
          </v-btn>
          <v-btn depressed @click="closeRateDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Upload dialog -->
    <BulkUpload
      v-if="bulkUploadDialog"
      v-model="bulkUploadDialog"
      uploadTo="vendor_contract_rate"
      :extraQueryParams="{ transporter_contract: contractId }"
      @refreshList="loadRates(contractId, 1)"
    />

    <!-- Logs dialog -->
    <v-dialog v-model="logsDialog" max-width="600">
      <v-card>
        <v-card-title class="secondary py-2 white--text">
          <span class="text-subtitle-1 font-weight-medium">Activity Logs</span>
          <v-spacer />
          <v-btn icon dark @click="logsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text style="max-height:480px;overflow-y:auto" class="pt-3">
          <v-timeline v-if="logs && logs.length" align-top dense class="px-0">
            <v-timeline-item v-for="(log, i) in logs" :key="i" color="primary" small>
              <v-row class="pt-1">
                <v-col>
                  <strong>{{ log.created | formatDate(true) }}</strong>
                  <div class="text-body-2">{{ log.message }}</div>
                  <div class="text-caption grey--text">— {{ log.updated_by }}</div>
                </v-col>
              </v-row>
            </v-timeline-item>
          </v-timeline>
          <div v-else class="pa-2 text-center grey--text">No logs to show</div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import Pagination from "@/components/General/Pagination.vue";
import BulkUpload from "@/components/General/BulkUpload.vue";
import * as XLSX from "xlsx";

export default {
  components: { AgGridVue, MultiSelect, Autocomplete, InputField, Pagination, BulkUpload },
  props: {
    contractId: { type: Number },
    permissions: { type: Object, default: () => ({}) },
  },
  watch: {
    contractId(val) {
      if (val) {
        this.contractDetails = {};
        this.contractRates = [];
        this.contractCharges = [];
        this.ratesPageNo = 1;
        this.ratesTotalItems = 0;
        this.logs = [];
        this.rateSearch = null;
        if (this.gridApi) this.gridApi.setQuickFilter("");
        this.getContractDetails(val);
        this.loadRates(val, 1);
        this.loadCharges(val);
        this.loadLogs(val);
      }
    },
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
  },
  data() {
    return {
      contractDetails: {},
      contractRates: [],
      ratesPageNo: 1,
      ratesPerPage: 20,
      ratesTotalItems: 0,
      headerExpanded: true,
      logs: [],

      gridApi: null,
      gridOptions: {
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },

      rateColumns: [
        { headerName: "Route", field: "lane_title", minWidth: 160, tooltipField: "lane_title" },
        { headerName: "Product", field: "container_type_title", minWidth: 140, tooltipField: "container_type_title" },
        { headerName: "Count", field: "container_count", minWidth: 80 },
        { headerName: "Rate", field: "rate", minWidth: 90 },
        {
          headerName: "Updated On",
          field: "modified",
          minWidth: 160,
          valueFormatter: (p) => p.value ? this.$globals.formatDate(p.value, true) : "-",
        },
        { headerName: "Updated By", field: "updated_by", minWidth: 120 },
        {
          headerName: "Actions",
          field: "actions",
          minWidth: 60,
          pinned: "right",
          cellRenderer: () => {
            const canEdit = this.permissions && this.permissions.change && this.contractDetails.status === "Active";
            if (canEdit) {
              return `<span style="color:var(--v-primary-base);cursor:pointer" data-action="edit">
                <i class="mdi mdi-pencil" style="font-size:18px"></i>
              </span>`;
            }
            return "";
          },
          onCellClicked: (params) => {
            const target = params.event.target.closest("[data-action]");
            if (!target) return;
            if (target.dataset.action === "edit") this.openEditRateDialog(params.data);
          },
        },
      ],

      rateDialog: false,
      rateFormValid: false,
      rateFormErrors: {},
      editingRate: {},

      allLaneList: [],
      laneCount: 0,
      laneLoading: false,
      allContainerTypes: [],

      rateSearch: null,
      activeTab: 0,

      logsDialog: false,
      bulkUploadDialog: false,
      bulkUpdateDialog: false,

      contractCharges: [],
      chargeGridApi: null,
      chargeGridOptions: {
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
      chargeColumns: [
        { headerName: "Charge", field: "charge_title", minWidth: 160, tooltipField: "charge_title" },
        { headerName: "Description", field: "description", minWidth: 180, tooltipField: "description" },
        { headerName: "Rev GL", field: "gl_no", minWidth: 100 },
        { headerName: "Cost GL", field: "cost_gl_no", minWidth: 100 },
        { headerName: "Tax %", field: "tax_percentage", minWidth: 80 },
        { headerName: "Tax", field: "tax", minWidth: 80 },
        { headerName: "Rate", field: "rate", minWidth: 90 },
        {
          headerName: "Actions",
          field: "actions",
          minWidth: 80,
          pinned: "right",
          cellRenderer: () => {
            const canEdit = this.permissions && this.permissions.change && this.contractDetails.status === "Active";
            if (canEdit) {
              return `
                <span style="color:var(--v-primary-base);cursor:pointer;margin-right:8px" data-action="edit">
                  <i class="mdi mdi-pencil" style="font-size:18px"></i>
                </span>
                <span style="color:#f44336;cursor:pointer" data-action="delete">
                  <i class="mdi mdi-delete" style="font-size:18px"></i>
                </span>`;
            }
            return "";
          },
          onCellClicked: (params) => {
            const target = params.event.target.closest("[data-action]");
            if (!target) return;
            if (target.dataset.action === "edit") this.openEditChargeDialog(params.data);
            if (target.dataset.action === "delete") this.deleteCharge(params.data.id);
          },
        },
      ],

      chargeDialog: false,
      chargeFormValid: false,
      chargeFormErrors: {},
      editingCharge: {},
      selectedCharge: null,
      allCharges: [],
    };
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      setTimeout(() => params.api.sizeColumnsToFit(), 100);
    },
    onRateSearch(val) {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(val || "");
      }
    },
    getContractDetails(id) {
      this.$bus.$emit("showLoader", true);
      this.$api.transporterContract
        .getTransporterContractDetails(id)
        .then((res) => {
          this.contractDetails = res.data;
          this.$bus.$emit("showLoader", false);
        })
        .catch(() => this.$bus.$emit("showLoader", false));
    },
    loadRates(id, page = 1) {
      const offset = this.ratesPerPage * (page - 1);
      this.$api.transporterContract
        .getTransporterContractRateList({ transporter_contract: id, limit: this.ratesPerPage, offset })
        .then((res) => {
          this.contractRates = res.data;
          this.ratesTotalItems = res.count;
        })
        .catch(() => {});
    },
    ratesPerPageChanged(val) {
      this.ratesPerPage = val;
      this.ratesPageNo = 1;
      this.loadRates(this.contractId, 1);
    },
    ratesPrevPage() {
      this.ratesPageNo--;
      this.loadRates(this.contractId, this.ratesPageNo);
    },
    ratesNextPage() {
      this.ratesPageNo++;
      this.loadRates(this.contractId, this.ratesPageNo);
    },
    loadLogs(id) {
      this.$api.transporterContract
        .getTransporterContractLogs(id)
        .then((res) => { this.logs = res.data; })
        .catch(() => {});
    },
    exportRates() {
      if (!this.contractRates.length) return;
      const rows = this.contractRates.map((r) => ({
        Route: r.lane_title || "",
        Product: r.container_type_title || "",
        Count: r.container_count,
        Rate: r.rate,
        "Updated On": r.modified ? this.$globals.formatDate(r.modified, true) : "",
        "Updated By": r.updated_by || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rates");
      XLSX.writeFile(wb, `${this.contractDetails.contract_id}_rates.xlsx`);
    },
    openAddRateDialog() {
      this.editingRate = { container_count: 1 };
      this.rateFormErrors = {};
      this.allLaneList = [];
      this.getLaneList({ limit: 10, offset: 0 });
      this.getContainerTypes();
      this.rateDialog = true;
    },
    openEditRateDialog(rate) {
      this.editingRate = {
        ...rate,
        lane: rate.lane ? { id: rate.lane, title: rate.lane_title } : null,
      };
      this.rateFormErrors = {};
      this.allLaneList = this.editingRate.lane ? [this.editingRate.lane] : [];
      this.getLaneList({ limit: 10, offset: 0 });
      this.getContainerTypes();
      this.rateDialog = true;
    },
    closeRateDialog() {
      this.rateDialog = false;
      this.editingRate = {};
    },
    submitRate() {
      const payload = {
        transporter_contract: this.contractDetails.id,
        lane: this.editingRate.lane && typeof this.editingRate.lane === "object"
          ? this.editingRate.lane.id : this.editingRate.lane,
        container_type: this.editingRate.container_type && typeof this.editingRate.container_type === "object"
          ? this.editingRate.container_type.id : this.editingRate.container_type,
        container_count: this.editingRate.container_count,
        rate: this.editingRate.rate,
      };
      const isEdit = !!this.editingRate.id;
      const call = isEdit
        ? this.$api.transporterContract.updateTransporterContractRate({ id: this.editingRate.id, data: payload })
        : this.$api.transporterContract.addTransporterContractRate(payload);

      this.$bus.$emit("showLoader", true);
      call
        .then(() => {
          this.closeRateDialog();
          this.loadRates(this.contractId, this.ratesPageNo);
          this.$bus.$emit("showToastMessage", { message: `Rate ${isEdit ? "updated" : "added"}!`, color: "success" });
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          this.rateFormErrors = err.data || {};
          this.$bus.$emit("showLoader", false);
        });
    },
    getLaneList(params = {}) {
      this.laneLoading = true;
      this.$api.lane
        .getLaneSelectList({ limit: 10, ...params })
        .then((res) => {
          const data = res.data || [];
          if (params.offset) {
            this.allLaneList = [...this.allLaneList, ...data];
          } else {
            const selected = this.editingRate.lane;
            const selectedId = selected && typeof selected === 'object' ? selected.id : selected;
            const alreadyInData = selectedId && data.find((l) => l.id === selectedId);
            this.allLaneList = selectedId && !alreadyInData && selected
              ? [selected, ...data]
              : data;
          }
          this.laneCount = res.count || 0;
        })
        .catch(() => {})
        .finally(() => { this.laneLoading = false; });
    },
    getContainerTypes() {
      if (this.allContainerTypes.length) return;
      this.$api.containerType
        .getContainerTypeList({ limit: 100 })
        .then((res) => { this.allContainerTypes = res.data; })
        .catch(() => {});
    },

    onChargeGridReady(params) {
      this.chargeGridApi = params.api;
      setTimeout(() => params.api.sizeColumnsToFit(), 100);
    },
    loadCharges(id) {
      this.$api.transporterContract
        .getTransporterContractChargeList({ transporter_contract: id, limit: "all" })
        .then((res) => { this.contractCharges = res.data; })
        .catch(() => {});
    },
    getCharges() {
      if (this.allCharges.length) return;
      this.$api.charge
        .getChargeSelectList({ limit: "all", is_active: "true", is_system_generated: "false" })
        .then((res) => { this.allCharges = res.data; })
        .catch(() => {});
    },
    openAddChargeDialog() {
      this.editingCharge = {};
      this.selectedCharge = null;
      this.chargeFormErrors = {};
      this.getCharges();
      this.chargeDialog = true;
    },
    openEditChargeDialog(charge) {
      this.editingCharge = { ...charge };
      this.selectedCharge = this.allCharges.find((c) => c.id === charge.charge) || null;
      this.chargeFormErrors = {};
      this.getCharges();
      this.chargeDialog = true;
    },
    closeChargeDialog() {
      this.chargeDialog = false;
      this.editingCharge = {};
      this.selectedCharge = null;
    },
    onChargeSelect(val) {
      if (val) {
        this.editingCharge.charge = val.id;
        this.editingCharge.charge_title = val.charge_title;
        this.editingCharge.description = val.description;
        this.editingCharge.gl_no = val.gl_no;
        this.editingCharge.cost_gl_no = val.cost_gl_no;
        this.editingCharge.tax_percentage = val.tax_percentage;
        this.editingCharge.rate = val.rate;
      } else {
        this.editingCharge = {};
      }
    },
    submitCharge() {
      const isEdit = !!this.editingCharge.id;
      const payload = {
        transporter_contract: this.contractDetails.id,
        charge: this.editingCharge.charge,
        charge_title: this.editingCharge.charge_title,
        description: this.editingCharge.description,
        gl_no: this.editingCharge.gl_no,
        tax_percentage: this.editingCharge.tax_percentage,
        rate: this.editingCharge.rate,
        tax_type: this.editingCharge.tax_type,
      };
      this.$bus.$emit("showLoader", true);
      const call = isEdit
        ? this.$api.transporterContract.updateTransporterContractCharge({ id: this.editingCharge.id, data: payload })
        : this.$api.transporterContract.addTransporterContractCharge(payload);

      call
        .then(() => {
          this.closeChargeDialog();
          this.loadCharges(this.contractId);
          this.$bus.$emit("showToastMessage", {
            message: `Charge ${isEdit ? "updated" : "added"}!`,
            color: "success",
          });
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          this.chargeFormErrors = err.data || {};
          this.$bus.$emit("showLoader", false);
        });
    },
    deleteCharge(id) {
      this.$bus.$emit("showLoader", true);
      this.$api.transporterContract
        .deleteTransporterContractCharge(id)
        .then(() => {
          this.loadCharges(this.contractId);
          this.$bus.$emit("showToastMessage", { message: "Charge deleted!", color: "success" });
          this.$bus.$emit("showLoader", false);
        })
        .catch(() => { this.$bus.$emit("showLoader", false); });
    },
  },
  mounted() {
    if (this.contractId) {
      this.getContractDetails(this.contractId);
      this.loadRates(this.contractId, this.ratesPageNo);
      this.loadCharges(this.contractId);
      this.loadLogs(this.contractId);
    }
  },
};
</script>
